import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { FaInstagram, FaYoutube, FaWhatsapp, FaShareAlt } from 'react-icons/fa';
import logo from '../assets/logo.webp';
import mapImage from '../assets/map.webp';

const Footer = () => {
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sunshine Travels',
          text: 'Discover luxury bespoke journeys with Sunshine Travels!',
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <footer className="bg-[#0B1120] text-white pt-16 pb-8">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Info */}
          <div className="space-y-6">
            <div className="flex flex-col items-start gap-2">
              <img src={logo} alt="Sunshine Travels" className="h-20 w-auto object-contain rounded-2xl" />
              <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[8px]">A Reason to Smile</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              Crafting world-class luxury experiences and bespoke travel itineraries since 2001. Your gateway to the extraordinary.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/sunshinetravelsmanipal?igsh=MXFzaGUyaGJzOTJmeA=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-gradient-to-tr hover:from-purple-500 hover:to-orange-500 transition-all duration-300 hover:scale-110 shadow-xl"
                aria-label="Instagram"
              >
                <FaInstagram size={18} />
              </a>
              <a
                href="https://www.youtube.com/@TheSunKissedPod?app=desktop"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF0000] transition-all duration-300 hover:scale-110 shadow-xl"
                aria-label="YouTube"
              >
                <FaYoutube size={18} />
              </a>
              <a
                href="https://wa.me/919880232901"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#25D366] transition-all duration-300 hover:scale-110 shadow-xl"
                aria-label="WhatsApp"
              >
                <FaWhatsapp size={18} />
              </a>
              <button
                onClick={handleShare}
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-orange-500 transition-all duration-300 hover:scale-110 shadow-xl"
                aria-label="Share"
              >
                <FaShareAlt size={16} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6 flex items-center gap-2">
              Quick <span className="text-orange-500 italic">Links</span>
            </h4>
            <ul className="space-y-3 text-slate-400 text-[13px] font-medium">
              <li><Link to="/destinations" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">Destinations</Link></li>
              <li><Link to="/services" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">Our Services</Link></li>
              <li><a href="https://www.youtube.com/@TheSunKissedPod" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">Podcast</a></li>
              <li><Link to="/about-us" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">About Us</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" className="hover:text-orange-500 transition-colors flex items-center gap-2 tracking-wide">Terms & Conditions</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6">
              Connect <span className="text-orange-500 italic">Direct</span>
            </h4>
            <ul className="space-y-4 text-slate-400 text-[13px] font-medium">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 border border-orange-500/20">
                  <MapPin size={16} className="text-orange-500" />
                </div>
                <span className="leading-relaxed">Academy Tower, Manipal, KA 576104 <br /><span className="text-[10px] opacity-60 font-bold uppercase tracking-widest">Hotel Green Park Suites</span></span>
              </li>
              <li className="flex items-center gap-3 group cursor-pointer">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 border border-orange-500/20 group-hover:bg-orange-500 transition-colors">
                  <Phone size={16} className="text-orange-500 group-hover:text-white" />
                </div>
                <span className="group-hover:text-white transition-colors tracking-widest">+91 98802 32901</span>
              </li>
              <li className="flex items-center gap-3 group cursor-pointer">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 border border-orange-500/20 group-hover:bg-orange-500 transition-colors">
                  <Mail size={16} className="text-orange-500 group-hover:text-white" />
                </div>
                <span className="group-hover:text-white transition-colors truncate">sunshinetravelsmanipal@gmail.com</span>
              </li>
            </ul>
          </div>

          {/* Map Preview */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6">
              Visit <span className="text-orange-500 italic">Us</span>
            </h4>
            <div className="group relative w-full h-40 rounded-[2rem] overflow-hidden shadow-2xl border border-white/5 bg-slate-900">
              <img
                src={mapImage}
                alt="Manipal Location"
                className="w-full h-full object-cover opacity-60 transition-all duration-1000 group-hover:scale-110 group-hover:opacity-100"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <a
                  href="https://www.google.com/maps/place/Sunshine+Travels+and+Holidays/@13.3552035,74.7619176,13z/data=!4m6!3m5!1s0x3bbca51cac348ccd:0x1f114dc0c7a1352c!8m2!3d13.351605!4d74.7875424!16s%2Fg%2F11gnsd3jpb?entry=ttu&g_ep=EgoyMDI2MDUxMi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-orange-500 text-white p-3 rounded-full shadow-2xl transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500"
                >
                  <MapPin size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 text-center text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] flex flex-col items-center justify-center gap-2">
          <p>© 2026 Sunshine Travels. All Rights Reserved.</p>
          <p>
            Powered by <a href="https://datavex.ai/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400 transition-colors underline decoration-orange-500/30 underline-offset-4">DataVex</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
